import { Space, Typography } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { formatMass } from 'core/utils/numbers';
import { getDeclarationUrl } from 'views/declarations/utils/getDeclarationUrl';
import { EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { H2 } from 'components/new-ui/base/typography/styles';
import PackageIcon from 'components/icons/PackageIcon';
import { colors } from 'theme';
import { InvalidIcon } from '../../Form.styles';
import { CardItem, ContentWrap, HeaderLeft, HeaderRow, IconWrap, InfoRow, Strong } from '../../styles';

const EMPTY_VALUE = '-';

const GROSS_MASS_FORMAT = { fractionDigits: 2, unit: '' };

type Props = {
    id: string;
    itemNumber: number;
    description?: string;
    hsCode?: string;
    grossMass?: number | string | null;
    packagesType?: string;
    viewOnly?: boolean | undefined;
    hasErrors?: boolean;
    onDelete?: (id: string) => Promise<void>;
};

const MasterItemListItem = ({
    id,
    itemNumber,
    description,
    hsCode,
    grossMass,
    packagesType,
    viewOnly,
    hasErrors,
    onDelete,
}: Props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { manifestId, jobId, declarationId } = useParams();

    const declarationUrl = getDeclarationUrl({
        manifestId,
        jobId,
        declarationId,
    });

    // Taken from the url, not from `viewOnly`: an amendment makes the fields editable while staying
    // under view-only, and routing the item out of it bounces back through a redirect.
    const inViewOnlyRoute = location.pathname.split('/').includes('view-only');
    const baseUrl = [declarationUrl, inViewOnlyRoute && 'view-only', 'master-items'].filter(Boolean).join('/');

    const navigateToMasterItem = () => {
        if (!baseUrl) return;
        navigate(`${baseUrl}/${id}`);
    };

    const editOrViewIcon = viewOnly ? <EyeOutlined /> : <EditOutlined />;
    const eidtOrViewTooltipText = viewOnly ? 'View' : 'Edit';

    return (
        <CardItem error={hasErrors}>
            <HeaderRow>
                <HeaderLeft>
                    <IconWrap>
                        <PackageIcon />
                    </IconWrap>
                    <ContentWrap>
                        <Space size={8} style={{ marginBottom: 2 }}>
                            <H2>
                                {itemNumber}. {description || EMPTY_VALUE}
                            </H2>
                            {hasErrors && <InvalidIcon style={{ color: colors.error }} title="This item is invalid" />}
                        </Space>
                        <InfoRow>
                            <Space size={4}>
                                <Typography.Text>HS Code: </Typography.Text>
                                <Strong>{hsCode || EMPTY_VALUE}</Strong>
                            </Space>

                            <Space size={4}>
                                <Typography.Text>Gross Mass: </Typography.Text>
                                <Strong>{formatMass(grossMass, GROSS_MASS_FORMAT) ?? EMPTY_VALUE}</Strong>
                            </Space>

                            <Space size={4}>
                                <Typography.Text>Type of Packages: </Typography.Text>
                                <Strong>{packagesType || EMPTY_VALUE}</Strong>
                            </Space>
                        </InfoRow>
                    </ContentWrap>
                </HeaderLeft>
                <Space size={8}>
                    <IconButton
                        onClick={navigateToMasterItem}
                        icon={editOrViewIcon}
                        tooltipText={eidtOrViewTooltipText}
                    />
                    {!viewOnly && (
                        <IconButton onClick={() => onDelete?.(id)} icon={<DeleteOutlined />} tooltipText="Delete" />
                    )}
                </Space>
            </HeaderRow>
        </CardItem>
    );
};

export default MasterItemListItem;
